import { useState, useRef } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { SpeakerHigh, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

// Shared fal.ai voice tool — same picker/preview/delivery/pace/emotion controls used everywhere
// audio or video is generated (Shee's chat, AI Video). Voice data comes from GET /api/voices.
export function VoicePicker({
  api, previewEndpoint = "/voices/preview", voices, deliveries, emotionTags,
  voiceId, onVoiceChange, delivery, onDeliveryChange, pace, onPaceChange,
  emotionTag, onEmotionTagChange, triggerClassName = "",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [previewingId, setPreviewingId] = useState(null);
  const audioRef = useRef(null);

  const selected = (voices || []).find((v) => v.id === voiceId);
  const isOrpheus = selected?.engine === "orpheus";
  const filtered = search.trim()
    ? (voices || []).filter((v) => `${v.label} ${v.group}`.toLowerCase().includes(search.trim().toLowerCase()))
    : (voices || []);

  const preview = async (id = voiceId) => {
    if (previewingId || !id) return;
    setPreviewingId(id);
    try {
      const r = await api.post(previewEndpoint, { voice_id: id, delivery, pace, emotion_tag: emotionTag });
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = r.data.audio_url;
      await audioRef.current.play();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't preview that voice");
    } finally { setPreviewingId(null); }
  };

  const selectVoice = (v) => {
    onVoiceChange(v.id);
    if (v.engine !== "orpheus" && emotionTag) onEmotionTagChange(null);
    setOpen(false);
    setSearch("");
  };

  return (
    <div className="space-y-2" data-testid="voice-picker">
      <div className="flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" data-testid="voice-picker-trigger"
              className={`h-8 text-xs bg-white border rounded-[8px] px-3 flex items-center justify-between gap-2 ${voiceId ? "border-[#d7e6e9] text-[#3C4048]" : "border-[#f0d98a] text-[#a16207]"} ${triggerClassName}`}>
              <span className="truncate">{selected?.label || "Choose a voice…"}</span>
              <span className="text-[#7A9090]">▾</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput value={search} onValueChange={setSearch} placeholder="Search & preview voices…" data-testid="voice-picker-search" />
              <CommandList>
                <CommandEmpty>No matching voice</CommandEmpty>
                {filtered.map((v) => (
                  <CommandItem key={v.id} value={v.id} onSelect={() => selectVoice(v)}
                    data-testid={`voice-picker-option-${v.id}`} className="flex items-center justify-between gap-2 cursor-pointer">
                    <div className="min-w-0">
                      <p className="text-sm text-[#3C4048] truncate">{v.label}</p>
                      <p className="text-[10px] text-[#7A9090] truncate">{v.group}</p>
                    </div>
                    <button type="button" onClick={(e) => { e.stopPropagation(); preview(v.id); }} disabled={!!previewingId}
                      className="shrink-0 text-[#3A7A8C] hover:text-[#5FA8B4] p-1" data-testid={`voice-picker-preview-${v.id}`}>
                      {previewingId === v.id
                        ? <CircleNotch size={14} className="animate-spin" />
                        : <SpeakerHigh size={14} weight="regular" />}
                    </button>
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <button
          type="button" onClick={() => preview()} disabled={!!previewingId || !voiceId}
          className="flex items-center gap-1.5 text-[#3A7A8C] text-xs font-semibold hover:underline disabled:opacity-50 whitespace-nowrap p-2 -m-2"
          data-testid="voice-picker-preview-btn"
        >
          {previewingId === voiceId && <CircleNotch size={12} className="animate-spin" />}
          {previewingId === voiceId ? "Generating sample…" : "Play sample"}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {(deliveries || []).length > 0 && (
          <div className="flex flex-wrap gap-0.5 bg-[#eef1f2] rounded-[8px] p-0.5" data-testid="voice-picker-delivery">
            {deliveries.map((d) => (
              <button key={d.id} type="button" onClick={() => onDeliveryChange(d.id)} data-testid={`voice-picker-delivery-${d.id}`}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-[6px] transition-colors ${delivery === d.id ? "bg-white text-[#3A7A8C] shadow-sm" : "text-[#7A9090]"}`}>
                {d.label}
              </button>
            ))}
          </div>
        )}
        {!isOrpheus && (
          <div className="flex items-center gap-2" data-testid="voice-picker-pace">
            <span className="text-[11px] text-[#7A9090]">Pace</span>
            <input type="range" min="0.7" max="1.3" step="0.05" value={pace}
              onChange={(e) => onPaceChange(Number(e.target.value))}
              className="w-24 accent-[#5FA8B4]" data-testid="voice-picker-pace-slider" />
            <span className="text-[11px] text-[#3C4048] w-9">{Number(pace).toFixed(2)}x</span>
          </div>
        )}
      </div>
      {isOrpheus && (
        <p className="text-[10px] text-[#7A9090]" data-testid="voice-picker-orpheus-pace-note">
          Pace isn't supported on Orpheus voices — use emotion tags below for expressiveness instead.
        </p>
      )}
      {selected?.engine === "orpheus" && (emotionTags || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="voice-picker-emotions">
          {emotionTags.map((t) => (
            <button key={t.id} type="button" onClick={() => onEmotionTagChange(emotionTag === t.id ? null : t.id)}
              data-testid={`voice-picker-emotion-${t.id}`}
              className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${emotionTag === t.id ? "bg-[#3A7A8C] text-white border-[#3A7A8C]" : "bg-white text-[#3A7A8C] border-[#d7e6e9]"}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}
      {selected && selected.engine !== "orpheus" && (
        <p className="text-[10px] text-[#7A9090]">Emotion tags (laugh, sigh…) only work on Orpheus voices.</p>
      )}
    </div>
  );
}
